import React from 'react';
import { TherapistAboutProps } from '../../types';

// Función helper para obtener iconos de especialización
const getSpecializationIcon = (area: string): string => {
  const iconMap: { [key: string]: string } = {
    'Trastornos del estado de ánimo': 'fas fa-cloud-rain',
    'Trastornos de ansiedad': 'fas fa-brain',
    'Problemas de pareja y familiares': 'fas fa-heart',
    'Traumas y estrés postraumático': 'fas fa-shield-alt',
    'Autoestima y desarrollo personal': 'fas fa-star',
    'Sexualidad y disfunciones sexuales': 'fas fa-venus-mars',
    'Violencia intrafamiliar': 'fas fa-shield-heart',
    'Adicciones comportamentales': 'fas fa-ban',
    'Terapia de Pareja': 'fas fa-heart',
    'Depresión': 'fas fa-cloud-rain',
    'Ansiedad': 'fas fa-brain',
    'Terapia Individual': 'fas fa-user',
    'Terapia Familiar': 'fas fa-users',
    'Autoestima': 'fas fa-star',
    'Trauma': 'fas fa-shield-alt',
    'Estrés': 'fas fa-compress-arrows-alt',
    'Duelo': 'fas fa-dove',
    'Adicciones': 'fas fa-ban',
    'Trastornos Alimentarios': 'fas fa-leaf',
    'Problemas de Sueño': 'fas fa-moon',
    'default': 'fas fa-circle'
  };
  
  for (const [key, icon] of Object.entries(iconMap)) {
    if (area.toLowerCase().includes(key.toLowerCase()) || key === 'default') {
      return icon;
    }
  }
  return iconMap.default;
};

// Función helper para obtener descripciones cortas
const getSpecializationDescription = (area: string): string => {
  const descriptionMap: { [key: string]: string } = {
    'Trastornos del estado de ánimo': 'Apoyo en momentos difíciles y cambios emocionales',
    'Trastornos de ansiedad': 'Manejo efectivo de preocupaciones y miedos',
    'Problemas de pareja y familiares': 'Fortalecimiento de vínculos y comunicación',
    'Traumas y estrés postraumático': 'Sanación profunda y recuperación integral',
    'Autoestima y desarrollo personal': 'Crecimiento y confianza en ti mismo',
    'Sexualidad y disfunciones sexuales': 'Bienestar y salud sexual integral',
    'Violencia intrafamiliar': 'Protección y recuperación emocional',
    'Adicciones comportamentales': 'Recuperación y control de conductas',
    'Terapia de Pareja': 'Fortalecimiento de vínculos',
    'Depresión': 'Apoyo en momentos difíciles',
    'Ansiedad': 'Manejo de preocupaciones',
    'Terapia Individual': 'Crecimiento personal',
    'Terapia Familiar': 'Armonía familiar',
    'Autoestima': 'Confianza y valor personal',
    'Trauma': 'Sanación y recuperación',
    'Estrés': 'Equilibrio y bienestar',
    'Duelo': 'Acompañamiento en pérdidas',
    'Adicciones': 'Recuperación y control',
    'Trastornos Alimentarios': 'Relación saludable con comida',
    'Problemas de Sueño': 'Descanso reparador'
  };
  
  for (const [key, description] of Object.entries(descriptionMap)) {
    if (area.toLowerCase().includes(key.toLowerCase())) {
      return description;
    }
  }
  return 'Apoyo profesional especializado';
};

/**
 * Componente About para la página individual del terapista
 * Muestra información detallada sobre el profesional
 */
const TherapistAbout: React.FC<TherapistAboutProps> = ({ therapist }) => {
  return (
    <section id="about" className="therapist-about">
      <div className="container">
        <div className="section-header animate-fade-in-up">
          <h2 className="section-title animate-reveal-text">Sobre {therapist.name.split(' ')[0]}</h2>
          <p className="section-subtitle animate-fade-in-up animate-delay-2">
            Experiencia y filosofía profesional de nuestro especialista
          </p>
        </div>

        <div className="about-content-single">
          {/* Columna única — información profesional */}
          <div className="about-content-section animate-fade-in-up animate-delay-3">
            {/* Biografía profesional */}
            <div className="bio-section">
              <h3>Biografía Profesional</h3>
              <p className="bio-text">
                {therapist.description}
              </p>
              
            </div>

            {/* Filosofía de trabajo */}
            <div className="philosophy-section">
              <h3>Filosofía de Trabajo</h3>
              <blockquote className="philosophy-quote">
                <p>"{therapist.professionalInfo.philosophy}"</p>
                <cite>- {therapist.name}</cite>
              </blockquote>
            </div>

            {/* Enfoque terapéutico */}
            <div className="approach-section">
              <h3>Enfoque Terapéutico</h3>
              <p className="approach-text">
                {therapist.professionalInfo.approach}
              </p>
            </div>

            {/* Metodologías */}
            <div className="methodology-section">
              <h3>Metodologías Utilizadas</h3>
              <div className="methodology-grid">
                {therapist.professionalInfo.methodology.map((method, index) => (
                  <div key={index} className="methodology-item">
                    <div className="methodology-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <span className="methodology-name">{method}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Áreas de especialización renovadas */}
            <div className="specialization-section-modern">
              <h3>Áreas de Especialización</h3>
              <div className="specialization-grid-modern">
                {therapist.professionalInfo.specialAreas?.map((area, index) => (
                  <div
                    key={index}
                    className="specialization-card-modern"
                    style={{ '--delay': `${index * 0.1}s` } as React.CSSProperties}
                  >
                    <div className="spec-icon">
                      <i className={getSpecializationIcon(area)}></i>
                    </div>
                    <div className="spec-text">
                      <h4>{area}</h4>
                      <p>{getSpecializationDescription(area)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

  );
};

export default TherapistAbout;